import { passwordSchema, passwordResetCompletedSchema } from '@canopee/contracts';
import { useMutation } from '@tanstack/react-query';
import { useState, type SyntheticEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { apiRequest } from '../../api/client.js';
import { formText } from '../../components/form-data.js';
import styles from '../login/login-screen.module.css';

export function ResetPasswordScreen() {
  const [searchParameters] = useSearchParams();
  const [formError, setFormError] = useState<string | null>(null);
  const token = searchParameters.get('token') ?? '';
  const email = searchParameters.get('email') ?? '';
  const reset = useMutation({
    mutationFn: (input: unknown) =>
      apiRequest('/api/v1/auth/password/reset', passwordResetCompletedSchema, {
        method: 'POST',
        body: input,
      }),
  });

  function submit(event: SyntheticEvent<HTMLFormElement, SubmitEvent>) {
    event.preventDefault();
    setFormError(null);
    const data = new FormData(event.currentTarget);
    const password = formText(data, 'password');
    const confirmation = formText(data, 'confirmation');
    if (token.length < 32 || email.length === 0) {
      setFormError('Le lien de réinitialisation est incomplet.');
      return;
    }
    const parsedPassword = passwordSchema.safeParse(password);
    if (!parsedPassword.success) {
      setFormError(parsedPassword.error.issues[0]?.message ?? 'Le mot de passe est insuffisant.');
      return;
    }
    if (password !== confirmation) {
      setFormError('Les deux mots de passe ne correspondent pas.');
      return;
    }
    reset.mutate({ token, email, password });
  }

  return (
    <main className={styles.page}>
      <form className={styles.card} onSubmit={submit}>
        <div>
          <p className={styles.eyebrow}>Canopée</p>
          <h1>Réinitialiser le mot de passe</h1>
          <p className={styles.hint}>
            12 caractères minimum, avec minuscule, majuscule, chiffre et caractère spécial.
          </p>
        </div>
        <label className={styles.field}>
          <span>Nouveau mot de passe</span>
          <input autoComplete="new-password" name="password" required type="password" />
        </label>
        <label className={styles.field}>
          <span>Confirmer le mot de passe</span>
          <input autoComplete="new-password" name="confirmation" required type="password" />
        </label>
        {reset.isSuccess ? (
          <p aria-live="polite" className={styles.success} role="status">
            Votre mot de passe a été modifié. Toutes les anciennes sessions ont été révoquées.
          </p>
        ) : null}
        {reset.isError || formError !== null ? (
          <p aria-live="polite" className={styles.error} role="alert">
            {reset.isError ? reset.error.message : formError}
          </p>
        ) : null}
        <button
          className={styles.submit}
          disabled={reset.isPending || reset.isSuccess}
          type="submit"
        >
          {reset.isPending ? 'Mise à jour…' : 'Modifier le mot de passe'}
        </button>
        <Link className={styles.link} to="/login">
          Retour à la connexion
        </Link>
      </form>
    </main>
  );
}
